import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  ServerStackIcon,
  CpuChipIcon,
  ChartBarIcon,
  BoltIcon,
  TagIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  EllipsisVerticalIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import type { Device, Customer, Asset, Gateway } from '../context/ManagementContext';
import managementService from '../api/managementService';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import DataTable from './DataTable';
import { useDebounce } from '../hooks/useDebounce';

const deviceSchema = z.object({
  slaveId: z.number().min(1, 'Slave ID must be a positive number'),
  name: z.string().optional(),
  description: z.string().optional(),
  gatewayId: z.string().min(1, 'Gateway is required'),
  customerId: z.string().nullable().optional(),
  assetId: z.string().nullable().optional(),
  deviceType: z.enum(['energy', 'water']).optional(),
});

type DeviceFormData = z.infer<typeof deviceSchema>;

export default function DeviceManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [selectedCustomerForFilter, setSelectedCustomerForFilter] = useState<Customer | null>(null);
  const [selectedAssetForFilter, setSelectedAssetForFilter] = useState<Asset | null>(null);
  const [selectedGatewayForFilter, setSelectedGatewayForFilter] = useState<Gateway | null>(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'name');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize')) || 20);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [isCustomerFilterSelectorOpen, setIsCustomerFilterSelectorOpen] = useState(false);
  const [isAssetFilterSelectorOpen, setIsAssetFilterSelectorOpen] = useState(false);
  const [isAssetSelectorOpen, setIsAssetSelectorOpen] = useState(false);
  const [isGatewaySelectorOpen, setIsGatewaySelectorOpen] = useState(false);
  const [isGatewayFilterSelectorOpen, setIsGatewayFilterSelectorOpen] = useState(false);
  const [selectedCustomerForForm, setSelectedCustomerForForm] = useState<Customer | null>(null);
  const [selectedAssetForForm, setSelectedAssetForForm] = useState<Asset | null>(null);
  const [selectedGatewayForForm, setSelectedGatewayForForm] = useState<Gateway | null>(null);
  const [isPopulationModalOpen, setIsPopulationModalOpen] = useState(false);
  const [isConfirmPopulationOpen, setIsConfirmPopulationOpen] = useState(false);
  const [populationInProgress, setPopulationInProgress] = useState(false);
  const [populationResult, setPopulationResult] = useState<any>(null);
  const [isForcePopulate, setIsForcePopulate] = useState(false);
  const [selectedDevicesForBulkAction, setSelectedDevicesForBulkAction] = useState<string[]>([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [selectedCustomerForBulkAssign, setSelectedCustomerForBulkAssign] = useState<Customer | null>(null);
  const [isBulkCustomerSelectorOpen, setIsBulkCustomerSelectorOpen] = useState(false);
  const queryClient = useQueryClient();

  // Function to update URL with current filter state
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (sortBy !== 'name') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);
    if (pageSize !== 20) params.set('pageSize', pageSize.toString());
    if (selectedCustomerForFilter) params.set('customerId', selectedCustomerForFilter.id);
    if (selectedAssetForFilter) params.set('assetId', selectedAssetForFilter.id);
    if (selectedGatewayForFilter) params.set('gatewayId', selectedGatewayForFilter.id);
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, statusFilter, typeFilter, sortBy, sortOrder, pageSize, selectedCustomerForFilter, selectedAssetForFilter, selectedGatewayForFilter, setSearchParams]);

  const { data: devicesData, isLoading, error } = useQuery({
    queryKey: ['devices', currentPage, debouncedSearchTerm, selectedCustomerForFilter?.id, selectedAssetForFilter?.id, selectedGatewayForFilter?.id, statusFilter, typeFilter, sortBy, sortOrder, pageSize],
    queryFn: () => managementService.device.getAll({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearchTerm || undefined,
      customerId: selectedCustomerForFilter?.id || undefined,
      assetId: selectedAssetForFilter?.id || undefined,
      gatewayId: selectedGatewayForFilter?.id || undefined,
      includeInactive: statusFilter === 'all',
      deviceType: typeFilter === 'all' ? undefined : typeFilter as 'energy' | 'water',
      sortBy: sortBy as 'name' | 'createdAt' | 'updatedAt',
      sortOrder: sortOrder as 'asc' | 'desc'
    }),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => managementService.customer.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['assets-all'],
    queryFn: () => managementService.asset.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  // Removed unused gateways query

  const createMutation = useMutation({
    mutationFn: (data: DeviceFormData) => {
      const { customerId, assetId, ...deviceData } = data;
      return managementService.device.create({
        id: crypto.randomUUID(),
        ...deviceData,
        customerId: customerId || undefined,
        assetId: assetId || undefined,
        isActive: true,
        isDeleted: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      // Handle unique constraint violation
      if (error?.response?.data?.error?.includes('UNIQUE constraint failed') ||
        error?.response?.data?.error?.includes('duplicate key')) {
        alert('A device with this Gateway and Slave ID combination already exists. Please use a different Slave ID.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeviceFormData }) => {
      const { customerId, assetId, ...deviceData } = data;
      return managementService.device.update(id, {
        ...deviceData,
        customerId: customerId || undefined,
        assetId: assetId || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setIsEditOpen(false);
      setSelectedDevice(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => managementService.device.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const batchPopulateMutation = useMutation({
    mutationFn: (onlyMissingNames: boolean = true) => managementService.device.batchPopulateNames(onlyMissingNames),
    onSuccess: (result) => {
      setPopulationResult(result);
      setPopulationInProgress(false);
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: () => {
      setPopulationResult({ success: false, error: 'Batch population failed' });
      setPopulationInProgress(false);
    },
  });

  const individualPopulateMutation = useMutation({
    mutationFn: (deviceId: string) => managementService.device.populateName(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const bulkAssignCustomerMutation = useMutation({
    mutationFn: ({ deviceIds, customerId }: { deviceIds: string[]; customerId: string | null }) => 
      managementService.device.bulkAssignCustomer(deviceIds, customerId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      setSelectedDevicesForBulkAction([]);
      setIsBulkAssignModalOpen(false);
      setSelectedCustomerForBulkAssign(null);
      alert(result.message);
    },
    onError: (error: any) => {
      alert(`Failed to assign devices: ${error?.response?.data?.error || error.message}`);
    },
  });

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<DeviceFormData>({
    resolver: zodResolver(deviceSchema),
  });

  const resetForm = () => {
    reset();
    setSelectedDevice(null);
    setSelectedCustomerForForm(null);
    setSelectedAssetForForm(null);
    setSelectedGatewayForForm(null);
  };

  const handleCreate = (data: DeviceFormData) => {
    if (!selectedGatewayForForm) {
      alert('Please select a gateway for this device.');
      return;
    }

    const formData = {
      ...data,
      gatewayId: selectedGatewayForForm.id,
      customerId: selectedCustomerForForm?.id || null,
      assetId: selectedAssetForForm?.id || null,
    };
    createMutation.mutate(formData);
  };

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setValue('slaveId', device.slaveId || 0);
    setValue('name', device.name || '');
    setValue('description', device.description || '');
    setValue('gatewayId', device.gateway?.id || device.gatewayId || '');
    setValue('deviceType', device.deviceType);

    // Set selected entities for the form
    const selectedCustomer = customers.find(c => c.id === device.customerId);
    const selectedAsset = assets.find(a => a.id === device.assetId);
    setSelectedCustomerForForm(selectedCustomer || null);
    setSelectedAssetForForm(selectedAsset || null);
    setSelectedGatewayForForm(device.gateway || null);

    setIsEditOpen(true);
  };

  const handleUpdate = (data: DeviceFormData) => {
    console.log('Form data received:', data);
    if (selectedDevice) {
      if (!selectedGatewayForForm) {
        alert('Please select a gateway for this device.');
        return;
      }

      const formData = {
        ...data,
        gatewayId: selectedGatewayForForm.id,
        customerId: selectedCustomerForForm?.id || null,
        assetId: selectedAssetForForm?.id || null,
      };
      console.log('Final form data:', formData);
      updateMutation.mutate({ id: selectedDevice.id, data: formData });
    }
  };

  const handleDelete = (device: Device) => {
    if (window.confirm(`Are you sure you want to delete device "${device.name}"?`)) {
      deleteMutation.mutate(device.id);
    }
  };


  const handleBatchPopulate = (forcePopulate = false) => {
    setIsForcePopulate(forcePopulate);
    setIsConfirmPopulationOpen(true);
  };

  const handleConfirmBatchPopulate = () => {
    setIsConfirmPopulationOpen(false);
    setPopulationInProgress(true);
    setPopulationResult(null);
    setIsPopulationModalOpen(true);
    batchPopulateMutation.mutate(!isForcePopulate); // Use opposite of force populate flag for onlyMissingNames
  };

  const handleIndividualPopulate = async (device: Device) => {
    try {
      const result = await individualPopulateMutation.mutateAsync(device.id);
      if (result.success) {
        alert(`Device name updated: "${result.data.previousName || 'empty'}" → "${result.data.newName}"`);
      } else {
        alert(`Failed to populate name: ${result.error}`);
      }
    } catch (error) {
      alert('Failed to populate device name');
    }
  };

  const handleBulkAssignCustomer = () => {
    if (selectedDevicesForBulkAction.length === 0) {
      alert('Please select at least one device');
      return;
    }
    setIsBulkAssignModalOpen(true);
  };

  const handleConfirmBulkAssignCustomer = () => {
    bulkAssignCustomerMutation.mutate({
      deviceIds: selectedDevicesForBulkAction,
      customerId: selectedCustomerForBulkAssign?.id || null
    });
  };

  const handleSelectAllDevices = (checked: boolean) => {
    if (checked) {
      const currentPageDeviceIds = devicesData?.data?.map(device => device.id) || [];
      setSelectedDevicesForBulkAction(currentPageDeviceIds);
    } else {
      setSelectedDevicesForBulkAction([]);
    }
  };

  const handleSelectDevice = (deviceId: string, checked: boolean) => {
    if (checked) {
      setSelectedDevicesForBulkAction(prev => [...prev, deviceId]);
    } else {
      setSelectedDevicesForBulkAction(prev => prev.filter(id => id !== deviceId));
    }
  };


  // Initialize entities from URL parameters on component mount
  useEffect(() => {
    const initializeFromUrl = async () => {
      const customerId = searchParams.get('customerId');
      const assetId = searchParams.get('assetId');
      const gatewayId = searchParams.get('gatewayId');
      
      // Wait for data to load before setting entities
      if (customers.length > 0 && customerId) {
        const customer = customers.find(c => c.id === customerId);
        setSelectedCustomerForFilter(customer || null);
      }
      
      if (assets.length > 0 && assetId) {
        const asset = assets.find(a => a.id === assetId);
        setSelectedAssetForFilter(asset || null);
      }
      
      // For gateways, we need to fetch them as needed since we don't have them in state
      if (gatewayId) {
        try {
          const gateway = await managementService.gateway.getById(gatewayId);
          setSelectedGatewayForFilter(gateway);
        } catch (error) {
          console.warn('Failed to load gateway from URL:', error);
        }
      }
    };
    
    initializeFromUrl();
  }, [searchParams, customers, assets]);

  // Update URL parameters whenever filter state changes
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  // Reset page when search, filters, or page size change (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, selectedCustomerForFilter?.id, selectedAssetForFilter?.id, selectedGatewayForFilter?.id, statusFilter, typeFilter, pageSize]);
  
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCustomerForFilter(null);
    setSelectedAssetForFilter(null);
    setSelectedGatewayForFilter(null);
    setStatusFilter('all');
    setTypeFilter('all');
    setSortBy('name');
    setSortOrder('asc');
    setPageSize(20);
    setCurrentPage(1);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const deviceColumns = [
    {
      key: 'select',
      header: '',
      width: '40px',
      render: (device: Device) => (
        <input
          type="checkbox"
          checked={selectedDevicesForBulkAction.includes(device.id)}
          onChange={(e) => handleSelectDevice(device.id, e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
      )
    },
    {
      key: 'device',
      header: t('device.device'),
      width: '280px',
      render: (device: Device) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 mr-3">
            <div className={`h-2 w-2 rounded-full ${device.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div 
              className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-purple-600 transition-colors text-left"
              onClick={() => handleEdit(device)}
            >
              {device.name || `Device ${device.slaveId}`}
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <span className="font-mono">{device.slaveId}</span>
              <span className="mx-1">•</span>
              <span className="truncate">{device.gateway?.name || device.gatewayId}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'type',
      header: 'Type',
      width: '80px',
      render: (device: Device) => (
        <div className="flex items-center justify-center">
          {device.deviceType === 'water' ? (
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
              <BeakerIcon className="h-4 w-4 text-blue-600" />
            </div>
          ) : device.deviceType === 'energy' ? (
            <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-lg">
              <BoltIcon className="h-4 w-4 text-yellow-600" />
            </div>
          ) : (
            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg">
              <DevicePhoneMobileIcon className="h-4 w-4 text-gray-600" />
            </div>
          )}
        </div>
      )
    },
    {
      key: 'assignment',
      header: t('device.customer'),
      render: (device: Device) => (
        <div className="text-sm">
          {device.customer?.name ? (
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-3 w-3 text-blue-500 mr-1 flex-shrink-0" />
              <span className="text-gray-900 truncate">{device.customer.name}</span>
            </div>
          ) : device.asset?.label ? (
            <div className="flex items-center">
              <ServerStackIcon className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
              <span className="text-gray-900 truncate">{device.asset.label}</span>
            </div>
          ) : (
            <span className="text-gray-400 text-xs">Unassigned</span>
          )}
        </div>
      )
    },
    {
      key: 'description',
      header: 'Description',
      render: (device: Device) => (
        <div className="text-sm text-gray-600 truncate max-w-xs">
          {device.description || '—'}
        </div>
      )
    },
    {
      key: 'lastUpdated',
      header: t('common.lastUpdated'),
      width: '100px',
      render: (device: Device) => (
        <div className="text-xs text-gray-500">
          {new Date(device.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit'
          })}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      sticky: 'right' as const,
      width: '60px',
      render: (device: Device) => (
        <div className="flex items-center justify-end">
          <Menu as="div" className="relative z-50">
            <MenuButton className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <MenuItems 
              anchor="bottom end"
              className="w-48 bg-white rounded-md shadow-xl border border-gray-200 py-1 z-[9999] focus:outline-none"
            >
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={`${focus ? 'bg-gray-50' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50`}
                    onClick={() => {
                      if (device.deviceType === 'water') {
                        navigate(`/management/devices/${device.id}/water-data`);
                      } else {
                        navigate(`/management/devices/${device.id}/energy-data`);
                      }
                    }}
                  >
                    <ChartBarIcon className="h-4 w-4 mr-3 text-gray-400" />
                    {t('device.viewData')}
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={`${focus ? 'bg-gray-50' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50`}
                    onClick={() => handleIndividualPopulate(device)}
                  >
                    <TagIcon className="h-4 w-4 mr-3 text-gray-400" />
                    {t('device.populateName')}
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={`${focus ? 'bg-gray-50' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50`}
                    onClick={() => handleEdit(device)}
                  >
                    <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
                    {t('common.edit')}
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={`${focus ? 'bg-red-50 text-red-700' : ''} flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700`}
                    onClick={() => handleDelete(device)}
                  >
                    <TrashIcon className="h-4 w-4 mr-3" />
                    {t('common.delete')}
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('device.title')}</h1>
        <div className="flex space-x-3">
          {/* Populate Names Dropdown */}
          <Menu as="div" className="relative inline-block text-left">
            <MenuButton
              disabled={populationInProgress}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              {populationInProgress ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Populating Names...
                </>
              ) : (
                <>
                  <TagIcon className="h-4 w-4 mr-2" />
                  Populate Names
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </MenuButton>

            <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <MenuItem>
                  {({ active }) => (
                    <button
                      onClick={() => handleBatchPopulate(false)}
                      disabled={populationInProgress}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50`}
                    >
                      <TagIcon className="mr-3 h-4 w-4" />
                      {t('device.populateName')}
                      <span className="ml-auto text-xs text-gray-500">(only empty)</span>
                    </button>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <button
                      onClick={() => handleBatchPopulate(true)}
                      disabled={populationInProgress}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50`}
                    >
                      <ArrowPathIcon className="mr-3 h-4 w-4" />
                      {t('device.forcePopulateName')}
                      <span className="ml-auto text-xs text-gray-500">(all devices)</span>
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('device.addDevice')}
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedDevicesForBulkAction.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-purple-900">
                {selectedDevicesForBulkAction.length} device(s) selected
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkAssignCustomer}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                Assign to Customer
              </button>
              <button
                onClick={() => setSelectedDevicesForBulkAction([])}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search devices by name, description, or slave ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Customer:</label>
                <button
                  type="button"
                  onClick={() => setIsCustomerFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  {selectedCustomerForFilter ? (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-blue-600" />
                      {selectedCustomerForFilter.name}
                    </>
                  ) : (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Customers
                    </>
                  )}
                </button>
                {selectedCustomerForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedCustomerForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    title="Clear customer filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Asset:</label>
                <button
                  type="button"
                  onClick={() => setIsAssetFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  {selectedAssetForFilter ? (
                    <>
                      <ServerStackIcon className="h-4 w-4 mr-2 text-green-600" />
                      {selectedAssetForFilter.label}
                    </>
                  ) : (
                    <>
                      <ServerStackIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Assets
                    </>
                  )}
                </button>
                {selectedAssetForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedAssetForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    title="Clear asset filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Gateway:</label>
                <button
                  type="button"
                  onClick={() => setIsGatewayFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  {selectedGatewayForFilter ? (
                    <>
                      <CpuChipIcon className="h-4 w-4 mr-2 text-purple-600" />
                      {selectedGatewayForFilter.name || selectedGatewayForFilter.id}
                    </>
                  ) : (
                    <>
                      <CpuChipIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Gateways
                    </>
                  )}
                </button>
                {selectedGatewayForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedGatewayForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    title="Clear gateway filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm px-3 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Type:</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm px-3 py-2"
                >
                  <option value="all">All Types</option>
                  <option value="energy">Energy</option>
                  <option value="water">Water</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm px-3 py-2"
                >
                  <option value="name">Name</option>
                  <option value="createdAt">Created Date</option>
                  <option value="updatedAt">Updated Date</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm px-3 py-2"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Per page:</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm px-3 py-2"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {(searchTerm || selectedCustomerForFilter || selectedAssetForFilter || selectedGatewayForFilter || statusFilter !== 'all' || typeFilter !== 'all' || sortBy !== 'name' || sortOrder !== 'asc' || pageSize !== 20) && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Results Summary */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                Showing {devicesData?.data?.length || 0} of {devicesData?.pagination?.total || 0} devices
                {debouncedSearchTerm && (
                  <span className="ml-2">for "{debouncedSearchTerm}"</span>
                )}
              </div>
              {isLoading && (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span>Searching...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Device List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Custom header with select all */}
        {devicesData?.data && devicesData.data.length > 0 && (
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedDevicesForBulkAction.length === devicesData.data.length && devicesData.data.length > 0}
                onChange={(e) => handleSelectAllDevices(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
              />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Select All ({devicesData.data.length})
              </span>
            </label>
          </div>
        )}

        <DataTable
          data={devicesData?.data || []}
          columns={deviceColumns}
          isLoading={isLoading}
          error={error}
          pagination={devicesData?.pagination}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          emptyStateIcon={DevicePhoneMobileIcon}
          emptyStateTitle="No devices found"
          emptyStateDescription="Create a new device to get started"
          className="shadow-none"
        />
      </div>


      {/* Create Device Dialog */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Create New Device
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Add a new device to your monitoring system
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleCreate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Basic Information
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway *
                      </label>
                      <input type="hidden" {...register('gatewayId')} />
                      <button
                        type="button"
                        onClick={() => setIsGatewaySelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedGatewayForForm ? (
                          <div className="flex items-center space-x-3">
                            <CpuChipIcon className="h-5 w-5 text-purple-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedGatewayForForm.name || selectedGatewayForForm.id}</span>
                              <span className="block text-sm text-gray-500">Gateway ID: {selectedGatewayForForm.id}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <CpuChipIcon className="h-5 w-5" />
                            <span>Select a gateway</span>
                          </div>
                        )}
                      </button>
                      {errors.gatewayId && (
                        <p className="mt-2 text-sm text-red-600">{errors.gatewayId.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="slaveId" className="block text-sm font-medium text-gray-700 mb-2">
                        Slave ID *
                      </label>
                      <input
                        type="number"
                        id="slaveId"
                        {...register('slaveId', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter slave ID"
                      />
                      {errors.slaveId && (
                        <p className="mt-2 text-sm text-red-600">{errors.slaveId.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Device Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        {...register('name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter device name"
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="description"
                        rows={4}
                        {...register('description')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter device description"
                      />
                    </div>

                    <div>
                      <label htmlFor="deviceType" className="block text-sm font-medium text-gray-700 mb-2">
                        Device Type
                      </label>
                      <select
                        id="deviceType"
                        {...register('deviceType')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      >
                        <option value="">Auto-detect from readings</option>
                        <option value="energy">Energy</option>
                        <option value="water">Water</option>
                      </select>
                      {errors.deviceType && (
                        <p className="mt-2 text-sm text-red-600">{errors.deviceType.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Leave as "Auto-detect" to automatically set type based on incoming telemetry data.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Assignment
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Customer
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsCustomerSelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedCustomerForForm ? (
                          <div className="flex items-center space-x-3">
                            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedCustomerForForm.name}</span>
                              <span className="block text-sm text-gray-500">{selectedCustomerForForm.description || 'No description'}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <BuildingOfficeIcon className="h-5 w-5" />
                            <span>Select a customer</span>
                          </div>
                        )}
                      </button>
                      {errors.customerId && (
                        <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsAssetSelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedAssetForForm ? (
                          <div className="flex items-center space-x-3">
                            <ServerStackIcon className="h-5 w-5 text-green-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedAssetForForm.label}</span>
                              <span className="block text-sm text-gray-500">{selectedAssetForForm.additional_info || 'No description'}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <ServerStackIcon className="h-5 w-5" />
                            <span>Select an asset</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Assignment Note */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      </div>
                      <div className="text-sm text-blue-800">
                        <strong>Note:</strong> Device must be assigned to either a customer or an asset. This determines the data collection scope and access permissions.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createMutation.isPending ? 'Creating Device...' : 'Create Device'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Edit Device
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Update device information and assignment
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleUpdate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Basic Information
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Device ID
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedDevice?.id || ''}
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-text select-all"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedDevice?.id) {
                              navigator.clipboard.writeText(selectedDevice.id);
                              // You could add a toast notification here
                            }
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                          title="Copy device ID"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Read-only identifier for this device</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway *
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsGatewaySelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedGatewayForForm ? (
                          <div className="flex items-center space-x-3">
                            <CpuChipIcon className="h-5 w-5 text-purple-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedGatewayForForm.name || selectedGatewayForForm.id}</span>
                              <span className="block text-sm text-gray-500">Gateway ID: {selectedGatewayForForm.id}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <CpuChipIcon className="h-5 w-5" />
                            <span>Select a gateway</span>
                          </div>
                        )}
                      </button>
                      {errors.gatewayId && (
                        <p className="mt-2 text-sm text-red-600">{errors.gatewayId.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-slaveId" className="block text-sm font-medium text-gray-700 mb-2">
                        Slave ID *
                      </label>
                      <input
                        type="number"
                        id="edit-slaveId"
                        {...register('slaveId', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter slave ID"
                      />
                      {errors.slaveId && (
                        <p className="mt-2 text-sm text-red-600">{errors.slaveId.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Device Name
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        {...register('name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter device name"
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="edit-description"
                        rows={4}
                        {...register('description')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter device description"
                      />
                    </div>

                    <div>
                      <label htmlFor="edit-deviceType" className="block text-sm font-medium text-gray-700 mb-2">
                        Device Type
                      </label>
                      <select
                        id="edit-deviceType"
                        {...register('deviceType')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      >
                        <option value="">Auto-detect from readings</option>
                        <option value="energy">Energy</option>
                        <option value="water">Water</option>
                      </select>
                      {errors.deviceType && (
                        <p className="mt-2 text-sm text-red-600">{errors.deviceType.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Leave as "Auto-detect" to automatically set type based on incoming telemetry data.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Assignment
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Customer
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsCustomerSelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedCustomerForForm ? (
                          <div className="flex items-center space-x-3">
                            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedCustomerForForm.name}</span>
                              <span className="block text-sm text-gray-500">{selectedCustomerForForm.description || 'No description'}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <BuildingOfficeIcon className="h-5 w-5" />
                            <span>Select a customer</span>
                          </div>
                        )}
                      </button>
                      {errors.customerId && (
                        <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsAssetSelectorOpen(true)}
                        className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                      >
                        {selectedAssetForForm ? (
                          <div className="flex items-center space-x-3">
                            <ServerStackIcon className="h-5 w-5 text-green-600" />
                            <div>
                              <span className="font-medium text-gray-900">{selectedAssetForForm.label}</span>
                              <span className="block text-sm text-gray-500">{selectedAssetForForm.additional_info || 'No description'}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-gray-500">
                            <ServerStackIcon className="h-5 w-5" />
                            <span>Select an asset</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Assignment Note */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      </div>
                      <div className="text-sm text-blue-800">
                        <strong>Note:</strong> Device must be assigned to either a customer or an asset. This determines the data collection scope and access permissions.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateMutation.isPending ? 'Updating Device...' : 'Update Device'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Bulk Assign Customer Modal */}
      <Dialog open={isBulkAssignModalOpen} onClose={() => setIsBulkAssignModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Assign Devices to Customer
              </DialogTitle>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  You are about to assign {selectedDevicesForBulkAction.length} devices to a customer.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsBulkCustomerSelectorOpen(true)}
                    className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                  >
                    {selectedCustomerForBulkAssign ? (
                      <div className="flex items-center space-x-3">
                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <span className="font-medium text-gray-900">{selectedCustomerForBulkAssign.name}</span>
                          <span className="block text-sm text-gray-500">{selectedCustomerForBulkAssign.description || 'No description'}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3 text-gray-500">
                        <BuildingOfficeIcon className="h-5 w-5" />
                        <span>Select a customer (or leave empty to unassign)</span>
                      </div>
                    )}
                  </button>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    </div>
                    <div className="ml-3 text-sm text-blue-800">
                      <p><strong>Note:</strong> If no customer is selected, devices will be unassigned from their current customer.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsBulkAssignModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBulkAssignCustomer}
                  disabled={bulkAssignCustomerMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {bulkAssignCustomerMutation.isPending ? 'Assigning...' : 'Assign Devices'}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Advanced Entity Selectors */}
      <AdvancedEntitySelector
        isOpen={isCustomerSelectorOpen}
        onClose={() => setIsCustomerSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForForm(entity as Customer);
          setSelectedAssetForForm(null); // Clear asset when customer changes
        }}
        entityType="customer"
        title="Select Customer"
        selectedId={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isAssetSelectorOpen}
        onClose={() => setIsAssetSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedAssetForForm(entity as Asset);
          setSelectedCustomerForForm(null); // Clear customer when asset changes
        }}
        entityType="asset"
        title="Select Asset"
        selectedId={selectedAssetForForm?.id}
        customerFilter={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isGatewaySelectorOpen}
        onClose={() => setIsGatewaySelectorOpen(false)}
        onSelect={(entity) => {
          const gateway = entity as Gateway;
          setSelectedGatewayForForm(gateway);
          setValue('gatewayId', gateway.id, { shouldValidate: true });

          // Auto-attribute customer from gateway
          if (gateway.customerId && !selectedCustomerForForm) {
            const gatewayCustomer = customers.find(c => c.id === gateway.customerId);
            if (gatewayCustomer) {
              setSelectedCustomerForForm(gatewayCustomer);
            }
          }
        }}
        entityType="gateway"
        title="Select Gateway"
        selectedId={selectedGatewayForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isCustomerFilterSelectorOpen}
        onClose={() => setIsCustomerFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForFilter(entity as Customer);
        }}
        entityType="customer"
        title="Filter by Customer"
        selectedId={selectedCustomerForFilter?.id}
      />

      <AdvancedEntitySelector
        isOpen={isAssetFilterSelectorOpen}
        onClose={() => setIsAssetFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedAssetForFilter(entity as Asset);
        }}
        entityType="asset"
        title="Filter by Asset"
        selectedId={selectedAssetForFilter?.id}
        customerFilter={selectedCustomerForFilter?.id}
      />

      <AdvancedEntitySelector
        isOpen={isGatewayFilterSelectorOpen}
        onClose={() => setIsGatewayFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedGatewayForFilter(entity as Gateway);
        }}
        entityType="gateway"
        title="Filter by Gateway"
        selectedId={selectedGatewayForFilter?.id}
      />

      <AdvancedEntitySelector
        isOpen={isBulkCustomerSelectorOpen}
        onClose={() => setIsBulkCustomerSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForBulkAssign(entity as Customer);
        }}
        entityType="customer"
        title="Select Customer for Bulk Assignment"
        selectedId={selectedCustomerForBulkAssign?.id}
      />


      {/* Confirmation Dialog */}
      <Dialog open={isConfirmPopulationOpen} onClose={() => setIsConfirmPopulationOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {isForcePopulate ? 'Confirm Force Population' : 'Confirm Batch Population'}
              </DialogTitle>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {isForcePopulate 
                    ? 'This will update names for ALL devices by fetching the latest data from their respective gateways. Existing names will be overwritten.'
                    : 'This will attempt to populate names for all devices that currently have missing names by fetching data from their respective gateways.'
                  }
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    </div>
                    <div className="ml-3 text-sm text-blue-800">
                      <p><strong>What will happen:</strong></p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Only devices with empty names will be updated</li>
                        <li>Gateway APIs will be contacted to fetch device information</li>
                        <li>Failed API calls will be logged but won't break the process</li>
                        <li>You'll see a detailed summary when complete</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-500">
                  This process may take a few moments depending on the number of devices and gateway response times.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsConfirmPopulationOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBatchPopulate}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700"
                >
                  {isForcePopulate ? 'Yes, Force Update All Names' : 'Yes, Populate Names'}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Population Results Modal */}
      <Dialog open={isPopulationModalOpen} onClose={() => setIsPopulationModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-2xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Batch Device Name Population
              </DialogTitle>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {populationInProgress ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-3">
                    <ArrowPathIcon className="h-8 w-8 text-purple-600 animate-spin" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">Populating device names...</p>
                      <p className="text-sm text-gray-500">This may take a moment depending on the number of devices</p>
                    </div>
                  </div>
                </div>
              ) : populationResult ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Summary</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{populationResult.data?.successCount || 0}</div>
                        <div className="text-sm text-gray-500">Successful</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{populationResult.data?.failureCount || 0}</div>
                        <div className="text-sm text-gray-500">Failed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-600">{populationResult.data?.skippedCount || 0}</div>
                        <div className="text-sm text-gray-500">Skipped</div>
                      </div>
                    </div>
                  </div>

                  {/* Gateway Errors */}
                  {populationResult.data?.gatewayErrors?.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-red-900 mb-2">Gateway Connection Errors</h3>
                      <div className="space-y-1">
                        {populationResult.data.gatewayErrors.map((error: any, index: number) => (
                          <div key={index} className="text-sm text-red-700">
                            <span className="font-medium">{error.gatewayId}:</span> {error.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {populationResult.success && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <p className="text-sm text-green-700">{populationResult.message}</p>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {!populationResult.success && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                        <p className="text-sm text-red-700">{populationResult.error || 'Population failed'}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsPopulationModalOpen(false);
                    setPopulationResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
